/**
 * Absurd Dilemmas.
 *
 * `id` is persisted on every `DilemmaVote` row, so **ids are permanent**.
 * Reordering this list is fine; renumbering is not — it would silently reattach
 * one dilemma's historical votes to a different question. Add new ones at the
 * end with the next free id and never reuse an id you have retired.
 *
 * There are deliberately no hard-coded crowd percentages here. The reveal shows
 * real counts from `DilemmaVote`; an earlier version shipped invented numbers
 * with a footnote admitting they were made up, which is worse than showing
 * nothing on a platform about facts.
 */
export type Dilemma = {
  id: number;
  scenario: string;
  pull: string;
  nothing: string;
  /** Shown after the choice, about the choice — not a claim about other people. */
  verdict: string;
};

export const DILEMMAS: Dilemma[] = [
  {
    id: 1,
    scenario:
      'A runaway trolley is heading toward five people tied to the track. You can pull a lever to divert it onto a side track, where one person is tied up instead.',
    pull: 'Pull the lever — one instead of five',
    nothing: 'Do nothing',
    verdict: 'The warm-up. It gets worse from here.'
  },
  {
    id: 2,
    scenario:
      'The trolley is heading toward five people. Pulling the lever diverts it onto a track holding one cat you have never met and will never meet.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'The cat has no idea any of this happened.'
  },
  {
    id: 3,
    scenario:
      'The trolley is heading toward one stranger. Pulling the lever sends it onto an empty track, but it will flatten your phone, which holds the only copy of every photo you have ever taken.',
    pull: 'Pull the lever — sacrifice the photos',
    nothing: 'Do nothing',
    verdict: 'Back up your photos.'
  },
  {
    id: 4,
    scenario:
      'The trolley is heading toward five people who are all loudly lying about which track they are on. Pulling the lever diverts it onto one person who has told the truth about everything, including this.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'Honesty is, apparently, a survival trait.'
  },
  {
    id: 5,
    scenario:
      'The trolley is heading toward the person who invented the pop-up ad. Pulling the lever diverts it onto an empty track and saves them.',
    pull: 'Pull the lever — save them',
    nothing: 'Do nothing',
    verdict: 'A surprisingly close vote, historically.'
  },
  {
    id: 6,
    scenario:
      'The trolley is heading toward five people. Pulling the lever diverts it onto an empty track. Also, for unrelated reasons, pulling the lever pays you 1,000 trons.',
    pull: 'Pull the lever and take the trons',
    nothing: 'Do nothing',
    verdict: 'Doing the right thing counts even when it pays.'
  },
  {
    id: 7,
    scenario:
      'The trolley is heading toward you. Pulling the lever diverts it onto a track with five strangers.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'Everyone is a utilitarian until it is their track.'
  },
  {
    id: 8,
    scenario:
      'The trolley is heading toward five people. Pulling the lever saves all of them and publishes your entire search history, unedited, to the front page of the internet.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'Five lives versus one browser. A real dilemma for some of you.'
  },
  {
    id: 9,
    scenario:
      'The trolley is heading toward an empty track. Pulling the lever diverts it onto a different empty track. Nothing is at stake. There is just a lever.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'Of course you pulled it. It was right there.'
  },
  {
    id: 10,
    scenario:
      'The trolley is heading toward five people. Pulling the lever stops it completely, but every fact on the internet becomes three percent wrong, forever.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'Somewhere, a fact checker just felt a chill.'
  },
  {
    id: 11,
    scenario:
      'The trolley is heading toward one person who, if they survive, will publish a hoax that forty million people believe. Pulling the lever saves them.',
    pull: 'Pull the lever — save them anyway',
    nothing: 'Do nothing',
    verdict: 'Punishing someone for a crime they have not committed yet.'
  },
  {
    id: 12,
    scenario:
      'The trolley is heading toward five people. You can pull the lever to save them. The lever is also a person.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'The lever would like a word.'
  },
  {
    id: 13,
    scenario:
      'The trolley is heading toward five people. Pulling the lever saves every one of them, and you will never be able to hear music again.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'A lifetime of silence, five lifetimes of noise.'
  },
  {
    id: 14,
    scenario:
      'The trolley is heading toward five identical copies of you. Pulling the lever diverts it onto one stranger.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'Five of you is arguably already the emergency.'
  },
  {
    id: 15,
    scenario:
      'The trolley is heading toward a priceless painting that no living person has ever seen. Pulling the lever diverts it onto the only photograph of that painting, which millions of people love.',
    pull: 'Pull the lever — save the painting',
    nothing: 'Do nothing',
    verdict: 'The original nobody sees, or the copy everybody does.'
  },
  {
    id: 16,
    scenario:
      'The trolley is heading toward nobody at all. Pulling the lever makes the trolley honk. The driver has specifically asked you not to make the trolley honk.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'No lives at stake. Just the driver, and your character.'
  },
  {
    id: 17,
    scenario:
      'The trolley is heading toward five people. The lever works perfectly. There is a small "WET PAINT" sign on it.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'Some of you genuinely hesitated.'
  },
  {
    id: 18,
    scenario:
      'The trolley is heading toward five bots that have convinced everyone they are human. Pulling the lever diverts it onto one human that everyone is convinced is a bot.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'Verification exists for exactly this situation.'
  },
  {
    id: 19,
    scenario:
      'The trolley is heading toward five people. If you pull the lever they all live, but for the rest of your life every website will reject your password.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'You have played the other game on this site. You know the cost.'
  },
  {
    id: 20,
    scenario:
      'The trolley is heading toward five strangers. Pulling the lever saves them, but you must read aloud every terms-of-service agreement you have ever accepted.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'That is roughly nine hundred hours of your voice.'
  },
  {
    id: 21,
    scenario:
      'The trolley is heading toward one person who is about to fix the single thing everyone complains about. If you save them, they will immediately break something else.',
    pull: 'Pull the lever — save them',
    nothing: 'Do nothing',
    verdict: 'This is just software development.'
  },
  {
    id: 22,
    scenario:
      'The trolley is heading toward five people. Pulling the lever saves them and also retroactively makes every joke you have ever told unfunny.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'For some of you this changes nothing.'
  },
  {
    id: 23,
    scenario:
      'The trolley is heading toward five people. The lever is available immediately after you finish reading a forty-page privacy policy.',
    pull: 'Read it and pull the lever',
    nothing: 'Do nothing',
    verdict: 'Nobody has ever read one. Today, five lives depended on it.'
  },
  {
    id: 24,
    scenario:
      'The trolley is heading toward five people who all sincerely believe the trolley is not real. Pulling the lever diverts it onto one person who has been warning everyone about the trolley for years.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'Being right early is its own kind of danger.'
  },
  {
    id: 25,
    scenario:
      'The trolley is heading toward the last remaining copy of a dictionary for a language with one speaker left. Pulling the lever diverts it onto that speaker’s parrot, which knows about forty words.',
    pull: 'Pull the lever — save the dictionary',
    nothing: 'Do nothing',
    verdict: 'Forty words is still a language, briefly.'
  },
  {
    id: 26,
    scenario:
      'The trolley is heading toward five people. Pulling the lever works, but you have to admit that an earlier answer in this quiz was wrong.',
    pull: 'Admit it and pull the lever',
    nothing: 'Do nothing',
    verdict: 'The hardest lever on the whole track.'
  },
  {
    id: 27,
    scenario:
      'The trolley is heading toward one person who is definitely, unquestionably about to tag their post with the wrong factiii.',
    pull: 'Pull the lever — save them',
    nothing: 'Do nothing',
    verdict: 'Moderation exists. The trolley is not moderation.'
  },
  {
    id: 28,
    scenario:
      'The trolley is heading toward five people. Pulling the lever saves them. A second, unlabelled lever next to it does something, and nobody knows what.',
    pull: 'Pull the correct lever',
    nothing: 'Pull the mystery lever',
    verdict: 'That was not the choice being offered, and you know it.'
  },
  {
    id: 29,
    scenario:
      'The trolley is heading toward five people. You are the only witness, and if you do nothing, nobody will ever know you were here.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict: 'Character is what you do when the record is empty.'
  },
  {
    id: 30,
    scenario:
      'The trolley is heading toward you, and honestly, you have had a good run. Pulling the lever diverts it onto a stranger who has not had a good run at all.',
    pull: 'Pull the lever',
    nothing: 'Do nothing',
    verdict:
      'Last one. However you answered, you had to decide what a life is worth.'
  }
];

export const diagnosis = (pulled: number, total: number) => {
  const share = total === 0 ? 0 : pulled / total;

  if (share >= 0.9) return 'You are a lever. Action at all costs.';
  if (share >= 0.7) return 'Broadly a utilitarian, with a few regrets.';
  if (share >= 0.4) return 'You decide case by case, which is the hardest way.';
  if (share >= 0.15)
    return 'You would rather let things happen than make them happen.';

  return 'You touched almost nothing. Bold, in its own way.';
};
